import {Alert} from 'src/design/atoms/Alert';
import {Loader} from 'src/design/atoms/Loader';
import {Text} from 'src/design/atoms/Text';
import {notifications} from '@mantine/notifications';
import {IconLock, IconLockOpen} from '@tabler/icons-react';
import {useEffect, useRef} from 'react';
import type {EntityIdentifierEnum} from 'src/api/ProactApiClient';
import {useGetIsLockedQuery} from 'src/redux/queries/lock';

export interface LockGuardProps {
  id: number;
  type: EntityIdentifierEnum;
  lockCache: boolean;
  onUnlock?: () => void;
  children?: React.ReactNode;
  /**
   * 'replace' (default): children are swapped out entirely for the lock
   * alert while locked. 'banner': the alert renders above the children,
   * which keep rendering underneath — used by pages that make their own
   * content read-only (disabled fields, hidden actions) instead of hiding
   * it, per the hide-don't-disable exception for lock state.
   */
  renderMode?: 'replace' | 'banner';
}

export const LockGuard: React.FC<LockGuardProps> = ({
  id,
  type,
  lockCache,
  onUnlock,
  children,
  renderMode = 'replace',
}) => {
  const {data, isLoading, isFetching} = useGetIsLockedQuery(
    {id, type},
    {
      skip: lockCache !== true,
      pollingInterval: 5000,
      // A cached status may predate the lock the parent just reported —
      // always confirm against the live endpoint on mount.
      refetchOnMountOrArgChange: true,
    }
  );
  const showLoader = isLoading || isFetching;

  // Only a locked -> unlocked transition observed by this instance shows the
  // unlock notification. Treating the first observed `false` as a fresh
  // unlock used to loop: notify -> parent refetch -> remount -> first `false`
  // again, at network speed, while the parent still reported the item locked.
  const prevLockedRef = useRef<boolean | undefined>(undefined);
  const syncedStaleParentRef = useRef(false);

  // A different entity's status must not be compared against the previous
  // one's — reset the tracking when the target changes.
  useEffect(() => {
    prevLockedRef.current = undefined;
    syncedStaleParentRef.current = false;
  }, [id, type]);

  useEffect(() => {
    const wasLocked = prevLockedRef.current;
    prevLockedRef.current = data;

    if (data !== false) {
      return;
    }

    if (wasLocked === true) {
      notifications.show({
        id: `lock-guard-unlocked-${type}-${id}`,
        color: 'yellow',
        icon: <IconLockOpen />,
        title: 'Item Unlocked',
        message: 'Your page will automatically update.',
      });
      onUnlock?.();
    } else if (wasLocked === undefined && !syncedStaleParentRef.current) {
      // The parent reported the item locked but the live status disagrees —
      // its data predates the unlock. Refresh it once (no notification) so it
      // stops reporting the lock; otherwise this guard polls indefinitely.
      syncedStaleParentRef.current = true;
      onUnlock?.();
    }
  }, [data, id, type, onUnlock]);

  if (lockCache !== true || data === false) {
    return <>{children}</>;
  }

  const alert = (
    <Alert
      variant="light"
      color="yellow"
      title="Item Locked"
      icon={showLoader ? <Loader size="xs" /> : <IconLock />}
      mb="md">
      <Text>
        This item is locked and may not be edited until background processing
        completes. This page will automatically update when the item unlocks.
      </Text>
    </Alert>
  );

  if (renderMode === 'banner') {
    return (
      <>
        {alert}
        {children}
      </>
    );
  }

  return alert;
};
